import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, MessageCircle, Loader2, FileCheck, Package } from 'lucide-react';
import { formatDataHoraSP } from '@fiodecobre/shared';
import { api } from '../../lib/api';
import { useSocket } from '../../lib/socket';
import { useStickyHeader } from '../../lib/useStickyHeader';
import gerarPdfOrcamento from '../../utils/gerarPdfOrcamento';
import { ModalEnviarWhatsApp } from '../../components/orcamento/ModalEnviarWhatsApp';

// Tela DEDICADA read-only da REMESSA em consignacao LEGADA ja emitida (DA-376, Fase 2). O contador
// VETOU a consignacao mercantil (hoje o caminho e a Venda CM, DA-381), entao NAO se emite remessa
// nova — sobraram so as 2 do Triunfo (devolvidas). Esta tela e' pura CONSULTA: cabecalho + qual NF-e
// ampara + tabela do que saiu/voltou + WhatsApp. As acoes de devolucao/acerto (retorno 1.918 / 5.114)
// foram REMOVIDAS junto com a maquina de emissao. Corrigir = cancelar a NF-e de remessa.
//
// Duas fontes de dado:
//  - GET /api/orcamentos/:id      -> cabecalho + itens ricos (pro PDF do WhatsApp e a Autorizacao) +
//                                    guarda de redirect (tipo='remessa' && remessa_emitida).
//  - GET /api/nfe/remessa-saldo/:id -> saldo por item (saiu/voltou/saldo, autoritativo) + qual NF-e
//                                    ampara (numero/serie/chave/data). Mesma fonte do modal de devolucao.

interface OrcItem {
  codigo_interno: string | null;
  produto_descricao: string;
  marca_nome: string | null;
  unidade_sigla: string | null;
  quantidade: number | string;
  preco_original: number | string;
  preco_unitario: number | string;
  desconto_percentual: number | string;
  subtotal: number | string;
  foto_url: string | null;
}
interface OrcDetalhe {
  numero: string;
  tipo?: string | null;
  remessa_emitida?: boolean | null;
  cliente_id: number | null;
  cliente_nome: string | null;
  cliente_cpf_cnpj: string | null;
  cliente_consignacao?: boolean | null;
  vendedor_nome: string | null;
  vendedor_login?: string | null;
  comprador: string | null;
  observacoes: string | null;
  total: number | string;
  itens: OrcItem[];
}

interface SaldoItem {
  orcamento_item_id: number;
  codigo: string | null;
  descricao: string;
  unidade: string;
  quantidade: string;      // saiu
  qtd_retornada: string;   // ja voltou
  saldo: string;           // quantidade - qtd_retornada
  valor_unitario: string;
}
interface SaldoResp {
  numero: string;
  cliente_nome: string | null;
  cliente_cidade: string | null;
  remessa_emitida: boolean;
  remessa_numero: number | null;
  remessa_serie: string | null;
  remessa_chave: string | null;
  remessa_emitido_em: string | null;
  itens: SaldoItem[];
}

function fmtQtd(v: string | number): string {
  const n = Number(v);
  return Number.isInteger(n) ? String(n) : n.toLocaleString('pt-BR', { maximumFractionDigits: 3 });
}

export function RemessaView() {
  const { id } = useParams<{ id: string }>();
  const orcId = Number(id);
  const navigate = useNavigate();
  const sticky = useStickyHeader();

  const [carregando, setCarregando] = useState(true);
  const [orc, setOrc] = useState<OrcDetalhe | null>(null);
  const [saldo, setSaldo] = useState<SaldoResp | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const [showModalWhatsApp, setShowModalWhatsApp] = useState(false);

  async function carregar(silencioso = false) {
    if (!orcId) return;
    if (!silencioso) setCarregando(true);
    try {
      const [d, s] = await Promise.all([
        api.get<OrcDetalhe>(`/api/orcamentos/${orcId}`),
        api.get<SaldoResp>(`/api/nfe/remessa-saldo/${orcId}`),
      ]);
      // Guarda de redirect: esta tela e' SO pra remessa com NF-e autorizada. Rascunho de remessa,
      // nota rejeitada/cancelada ou orcamento de venda voltam pra OrcamentoNovo (editavel).
      if (d.tipo !== 'remessa' || d.remessa_emitida !== true) {
        navigate(`/orcamentos/${orcId}`, { replace: true });
        return;
      }
      setOrc(d);
      setSaldo(s);
    } catch (e: any) {
      setErro(e?.message || 'Nao foi possivel carregar a remessa.');
    } finally {
      setCarregando(false);
    }
  }

  useEffect(() => { void carregar(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [orcId]);

  // Recarga de fundo quando o backend sinaliza mudanca de pedido (mesmo evento do caixa; ex.: a
  // NF-e de remessa legada cancelada em outra tela). Silenciosa — nao pisca a tela (DA-298).
  const { socket } = useSocket();
  useEffect(() => {
    if (!socket) return;
    const fn = () => void carregar(true);
    socket.on('caixa:pedidos_atualizados', fn);
    return () => { socket.off('caixa:pedidos_atualizados', fn); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, orcId]);

  const itensSaldo = saldo?.itens ?? [];
  const { totalSaldo, totalVoltou } = useMemo(() => {
    let s = 0, v = 0;
    for (const it of itensSaldo) { s += Number(it.saldo); v += Number(it.qtd_retornada); }
    return { totalSaldo: s, totalVoltou: v };
  }, [itensSaldo]);

  // Status da consignacao — mesma regra da aba Remessas (OrcamentosHome). Sem verde/vermelho (daltonismo).
  const status = totalSaldo <= 0 ? { txt: 'devolvida', cls: 'badge-cinza' }
    : totalVoltou > 0 ? { txt: 'parcial', cls: 'badge-amarelo' }
    : { txt: 'em aberto', cls: 'badge-amarelo' };

  function montarDadosPdf() {
    if (!orc) return null;
    // Remessa: preco travado, sem desconto de venda. O PDF sai como um orcamento normal (mesmo
    // gerador), amparado pela mesma faixa promocional do rodape.
    return {
      numero: orc.numero,
      created_at: new Date().toISOString(),
      vendedor_nome: orc.vendedor_login || orc.vendedor_nome || '',
      cliente_nome: orc.cliente_nome || 'Consumidor',
      forma_pagamento_nome: 'Remessa em consignação',
      subtotal: Number(orc.total),
      desconto_total_percentual: 0,
      desconto_total_valor: 0,
      total: Number(orc.total),
      observacoes: orc.observacoes || '',
      itens: orc.itens.map((it) => ({
        codigo_interno: it.codigo_interno,
        produto_descricao: it.produto_descricao,
        marca_nome: it.marca_nome,
        unidade_sigla: it.unidade_sigla,
        quantidade: it.quantidade,
        preco_original: it.preco_original,
        preco_unitario: it.preco_unitario,
        desconto_percentual: it.desconto_percentual,
        subtotal: it.subtotal,
        foto_url: it.foto_url,
      })),
    };
  }

  if (carregando) {
    return (
      <div className="flex items-center justify-center gap-2 py-16 text-gray-500">
        <Loader2 size={18} className="animate-spin" /> Carregando remessa…
      </div>
    );
  }
  if (erro || !orc || !saldo) {
    return (
      <div>
        <div className="pagina-header">
          <h2 className="pagina-titulo">
            <button type="button" className="btn-icon" onClick={() => navigate('/orcamentos', { state: { aba: 'remessas' } })} aria-label="Voltar"><ArrowLeft size={20} /></button>
            Remessa
          </h2>
        </div>
        <div className="mt-4 text-sm rounded-lg px-3 py-2" style={{ backgroundColor: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca' }}>
          {erro || 'Remessa nao encontrada.'}
        </div>
      </div>
    );
  }

  const clienteNome = orc.cliente_nome || 'Consumidor';
  const nfeNumero = saldo.remessa_numero != null ? String(saldo.remessa_numero).padStart(6, '0') : '—';

  return (
    <div>
      <div ref={sticky.headerRef} style={sticky.headerStyle}>
        <div className="pagina-header">
          <h2 className="pagina-titulo" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <button type="button" className="btn-icon" onClick={() => navigate('/orcamentos', { state: { aba: 'remessas' } })} aria-label="Voltar"><ArrowLeft size={20} /></button>
            <Package size={20} style={{ display: 'inline' }} /> Remessa {orc.numero}
            <span className={`badge ${status.cls}`} style={{ fontSize: 12 }} title="Status da consignação">{status.txt}</span>
          </h2>
          <div className="pagina-acoes">
            <button
              onClick={() => setShowModalWhatsApp(true)}
              className="flex items-center gap-2 bg-green-500 text-white rounded-lg px-4 py-2 text-sm font-medium hover:bg-green-600 transition"
            >
              <MessageCircle size={16} /> WhatsApp
            </button>
          </div>
        </div>
      </div>

      {/* Faixa: qual NF-e ampara o material que saiu. */}
      <div
        className="flex flex-wrap items-center gap-x-5 gap-y-1 rounded-lg px-4 py-2.5 mb-3 text-sm"
        style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border)' }}
      >
        <span className="flex items-center gap-1.5 font-semibold" style={{ color: 'var(--primary)' }}>
          <FileCheck size={15} /> NF-e de remessa
        </span>
        <span><b>Nº</b> {nfeNumero}{saldo.remessa_serie ? ` · série ${saldo.remessa_serie}` : ''}</span>
        {saldo.remessa_emitido_em && <span><b>Emitida</b> {formatDataHoraSP(saldo.remessa_emitido_em)}</span>}
        <span style={{ color: 'var(--text-muted)' }}>{clienteNome}</span>
        {saldo.remessa_chave && (
          <span className="tabular-nums" style={{ color: 'var(--text-muted)', fontSize: 11 }} title="Chave de acesso">
            {saldo.remessa_chave}
          </span>
        )}
      </div>

      {/* Tabela read-only: o que saiu, o que voltou, o que ainda esta com o cliente. */}
      <div className="rounded-xl shadow-sm border overflow-hidden" style={{ borderColor: 'var(--border)', backgroundColor: 'var(--panel-bg)' }}>
        <table className="w-full text-sm" style={{ borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ color: 'var(--text-muted)' }} className="text-[11px] uppercase tracking-wide">
              <th className="text-left font-bold px-3 py-2.5" style={{ borderBottom: '1.5px solid var(--border)' }}>Produto</th>
              <th className="text-center font-bold px-3 py-2.5" style={{ borderBottom: '1.5px solid var(--border)', width: 60 }}>Un.</th>
              <th className="text-right font-bold px-3 py-2.5" style={{ borderBottom: '1.5px solid var(--border)', width: 110 }}>Qtd. enviada</th>
              <th className="text-right font-bold px-3 py-2.5" style={{ borderBottom: '1.5px solid var(--border)', width: 100 }}>Retornado</th>
              <th className="text-right font-bold px-3 py-2.5" style={{ borderBottom: '1.5px solid var(--border)', width: 130 }}>Saldo c/ cliente</th>
            </tr>
          </thead>
          <tbody>
            {itensSaldo.map((it) => {
              const saldoN = Number(it.saldo);
              const zerado = saldoN <= 0;
              return (
                <tr key={it.orcamento_item_id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td className="px-3 py-2">
                    <div className="font-semibold" style={{ color: zerado ? 'var(--text-muted)' : 'var(--page-fg)' }}>{it.descricao}</div>
                    <div className="text-[11px]" style={{ color: 'var(--text-muted)' }}>{it.codigo}</div>
                  </td>
                  <td className="px-3 py-2 text-center" style={{ color: 'var(--text-muted)' }}>{it.unidade}</td>
                  <td className="px-3 py-2 text-right tabular-nums" style={{ color: 'var(--page-fg)' }}>{fmtQtd(it.quantidade)}</td>
                  <td className="px-3 py-2 text-right tabular-nums" style={{ color: 'var(--text-muted)' }}>{fmtQtd(it.qtd_retornada)}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-extrabold">
                    {zerado
                      ? <span style={{ color: 'var(--text-muted)' }} title="Cliente devolveu tudo">✓ devolvido</span>
                      : <span style={{ backgroundColor: '#fde68a', color: '#000', padding: '1px 8px', borderRadius: 6 }}>{fmtQtd(it.saldo)}</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr style={{ backgroundColor: 'var(--card-bg)' }}>
              <td className="px-3 py-2.5 text-sm font-semibold" colSpan={2} style={{ color: 'var(--text-muted)' }}>
                {itensSaldo.length} {itensSaldo.length === 1 ? 'item' : 'itens'}
              </td>
              <td className="px-3 py-2.5" />
              <td className="px-3 py-2.5 text-right text-[11px] uppercase tracking-wide font-bold" style={{ color: 'var(--text-muted)' }}>Saldo total</td>
              <td className="px-3 py-2.5 text-right tabular-nums font-extrabold" style={{ color: 'var(--page-fg)' }}>{fmtQtd(totalSaldo)}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      {showModalWhatsApp && (
        <ModalEnviarWhatsApp
          orcamentoId={orcId}
          numero={orc.numero}
          clienteId={orc.cliente_id ?? undefined}
          clienteNome={clienteNome}
          obterPdfBase64={async () => {
            const dados = montarDadosPdf();
            if (!dados) throw new Error('Remessa sem dados para o PDF.');
            const doc = await gerarPdfOrcamento(dados);
            const dataUri = doc.output('datauristring');
            const base64 = dataUri.split('base64,')[1] ?? '';
            return { base64, filename: `${orc.numero || 'remessa'}.pdf` };
          }}
          onClose={() => setShowModalWhatsApp(false)}
        />
      )}
    </div>
  );
}
